import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';

/**
 * Landing page for the verification link.
 * Expects: /verify-email?token=...
 */
function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('Verifying your email…');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Verification token missing. Please use the link from your email.');
        return;
      }

      try {
        // Prefer API wrapper if present.
        let data;
        if (typeof authAPI.verifyEmail === 'function') {
          const res = await authAPI.verifyEmail(token);
          data = res.data;
        } else {
          // Fallback direct fetch (in case authAPI wasn’t updated yet)
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/verify-email?token=${encodeURIComponent(token)}`
          );
          data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Failed to verify email');
        }

        if (cancelled) return;

        // If backend returns a JWT, log the user in immediately.
        if (data?.token) {
          const loginResult = await loginWithToken(data.token);
          if (!loginResult.success) {
            setStatus('success');
            setMessage('Email verified. Please log in.');
            return;
          }
          setStatus('success');
          setMessage('Email verified! Redirecting…');
          navigate('/dashboard', { replace: true });
          return;
        }

        // If no token is returned, still treat as verified and send them to login.
        setStatus('success');
        setMessage(data?.message || 'Email verified. Please log in.');
      } catch (err) {
        console.error('Verify email error:', err);
        setStatus('error');
        setMessage(err.message || 'Verification failed. Please try again.');
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token, loginWithToken, navigate]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Email Verification</h1>
        <h2>
          {status === 'verifying'
            ? 'One moment…'
            : status === 'success'
            ? 'Success'
            : 'Couldn’t Verify'}
        </h2>

        <p style={{ color: '#666', marginTop: 10 }}>{message}</p>

        {status !== 'verifying' && (
          <div style={{ marginTop: 18 }}>
            {status === 'success' ? (
              <>
                <Link
                  to="/dashboard"
                  className="btn-primary"
                  style={{ display: 'inline-block', textAlign: 'center' }}
                >
                  Continue
                </Link>
                <p className="auth-link" style={{ marginTop: 14 }}>
                  If you aren’t redirected, you can also <Link to="/login">log in</Link>.
                </p>
              </>
            ) : (
              <>
                <Link
                  to="/verify-email-sent"
                  className="btn-primary"
                  style={{ display: 'inline-block', textAlign: 'center' }}
                >
                  Resend Verification Email
                </Link>
                <p className="auth-link" style={{ marginTop: 14 }}>
                  <Link to="/login">Back to Login</Link>
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
